import { useMemo } from "react";

import type { AgChartOptions } from "ag-charts-community";

import { AgChartPanel, buildAgOptions, useIsDark } from "@/components/cinch/charts";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { useAccuracy, type AccuracyRow } from "../api";
import { accuracyDataset, fmtUsd, weekLabel } from "../lib";

const TOTAL_INFLOWS = "TOTAL INFLOWS";
const TOTAL_OUTFLOWS = "TOTAL OUTFLOWS";
const NET_CASH = "NET CASH CHANGE";
const ENDING_CASH = "ENDING CASH BALANCE";

/** One of the workbook's three CF Charts: forecast at each horizon against
 *  what actually happened. Gaps are real — a horizon only has a point once a
 *  lock that far back captured it. */
function HorizonChart({
  title, description, rows, lineItem,
}: {
  title: string;
  description: string;
  rows: AccuracyRow[];
  lineItem: string;
}) {
  const isDark = useIsDark();
  const dataset = useMemo(() => accuracyDataset(rows, lineItem), [rows, lineItem]);
  const options = useMemo<AgChartOptions>(
    () => buildAgOptions(dataset, "line", isDark),
    [dataset, isDark],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {dataset.rows.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
            Nothing logged yet. Each lock adds a point at +1, +6 and +13 weeks, so the lines
            fill in as weeks close.
          </p>
        ) : (
          <AgChartPanel options={options} />
        )}
      </CardContent>
    </Card>
  );
}

/** Per-line-item forecast vs actual — the "why did we miss?" table.
 *
 *  Jim's answer on the call was that it's almost always Customer Collections:
 *  outflows forecast well, inflows swing. This makes that visible rather than
 *  something you have to already know. */
function VarianceTable({ rows }: { rows: AccuracyRow[] }) {
  const scored = useMemo(
    () =>
      rows
        .filter((r) => r.actual !== null && r.horizon === "1wk")
        .map((r) => ({
          ...r,
          variance: (r.actual ?? 0) - r.forecast,
          pct: r.forecast === 0 ? null : ((r.actual ?? 0) - r.forecast) / Math.abs(r.forecast),
        }))
        .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance)),
    [rows],
  );

  const columns: DataTableColumn<(typeof scored)[number]>[] = [
    {
      key: "target_week_ending",
      header: "Week",
      render: (r) => weekLabel(r.target_week_ending, true),
    },
    { key: "line_item", header: "Line item" },
    {
      key: "forecast",
      header: "Forecast",
      numeric: true,
      align: "right",
      render: (r) => fmtUsd(r.forecast),
    },
    {
      key: "actual",
      header: "Actual",
      numeric: true,
      align: "right",
      render: (r) => fmtUsd(r.actual ?? 0),
    },
    {
      key: "variance",
      header: "Variance",
      numeric: true,
      align: "right",
      render: (r) => (
        <span className={r.variance < 0 ? "text-danger" : "text-success"}>
          {fmtUsd(r.variance)}
        </span>
      ),
    },
    {
      key: "pct",
      header: "Var %",
      numeric: true,
      align: "right",
      render: (r) =>
        r.pct === null ? "—" : `${(r.pct * 100).toFixed(1)}%`,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Biggest misses (1-week horizon)</CardTitle>
        <CardDescription>
          Where last week's forecast and the bank disagreed, largest first. Outflows are
          usually close; the swing is nearly always Customer Collections.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={scored}
          rowKey={(r) => `${r.target_week_ending}-${r.line_item}`}
          emptyMessage="No week has both a forecast and an actual yet — lock two consecutive weeks to see variance."
        />
      </CardContent>
    </Card>
  );
}

/** Forecast accuracy — the workbook's History tab and CF Charts.
 *
 *  Every lock records what the model predicted at +1, +6 and +13 weeks; when
 *  that week closes, the actual fills in alongside. Accuracy is therefore
 *  measured against what we actually said at the time, not a reconstruction. */
export function AccuracyTab() {
  const { data: rows, isLoading } = useAccuracy();
  const all = useMemo(() => rows ?? [], [rows]);

  const locked = useMemo(
    () => new Set(all.map((r) => r.snapshot_date)).size,
    [all],
  );
  const withActuals = useMemo(
    () => new Set(all.filter((r) => r.actual !== null).map((r) => r.target_week_ending)).size,
    [all],
  );

  if (isLoading) {
    return (
      <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
        Loading accuracy log…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>
            Forecast accuracy
            <Badge variant="neutral" className="ml-2">{all.length} rows</Badge>
          </CardTitle>
          <CardDescription>
            {locked} week{locked === 1 ? "" : "s"} locked · {withActuals} target week
            {withActuals === 1 ? "" : "s"} closed and scored. Each lock captures the forecast at
            +1, +6 and +13 weeks as a stored value, so accuracy is measured against what was
            actually claimed at the time — later changes to the model can't restate it.
          </CardDescription>
        </CardHeader>
      </Card>

      <HorizonChart
        title="Inflows"
        description="Forecast at each horizon vs what the bank collected."
        rows={all}
        lineItem={TOTAL_INFLOWS}
      />
      <HorizonChart
        title="Outflows"
        description="Historically the well-forecast side — payroll and vendors run on calendars."
        rows={all}
        lineItem={TOTAL_OUTFLOWS}
      />
      <HorizonChart
        title="Net cash change"
        description="Where the two sides land together."
        rows={all}
        lineItem={NET_CASH}
      />
      <HorizonChart
        title="Ending cash balance"
        description="The number the accuracy percentages on the Summary tab are based on."
        rows={all}
        lineItem={ENDING_CASH}
      />

      <VarianceTable rows={all} />
    </div>
  );
}
